import apiEndPoints from "../../routeConfig";
import { ApiService } from "../../../service/api.service";
import { attempt } from "../../../service/apiResult";

const apiService = new ApiService();

export const countryManagementCreateOrUpdate = async (data, editMode, id,) => {
  const url = editMode === true
    ? `${apiEndPoints.countryManagementCreateOrUpdate}/${id}`
    : `${apiEndPoints.countryManagementCreateOrUpdate}`;

  try {
    const response = editMode === true
      ? await apiService.update(url, data)
      : await apiService.post(url, data);
    return { success: true, data: response };
  } catch (error) {
    return { success: false, error };
  }
}

export const getAllcountryManagement = async (page = 0, size = 10) => {
  try {
    const response = await apiService.get(`${apiEndPoints.getAllcountryManagement}?page=${page}&size=${size}`,);
    return response;
  } catch (error) {
    return error;
  }
};

// Fetches every configured country across all pages and returns a flat array.
// Used where a countryId needs to be resolved to a display name (e.g. subscription
// plan list/detail screens).
export const getAllCountriesFlat = async () => {
  const pageSize = 100;
  let page = 0;
  let hasNextPage = true;
  const allCountries = [];

  try {
    while (hasNextPage) {
      const res = await getAllcountryManagement(page, pageSize);
      const countries = Array.isArray(res?.data?.content) ? res.data.content : [];
      allCountries.push(...countries);

      const totalPages = Number(res?.data?.totalPages || 0);
      if (totalPages > 0) {
        hasNextPage = page + 1 < totalPages;
      } else {
        hasNextPage = countries.length === pageSize;
      }

      page += 1;
      if (page > 100) {
        hasNextPage = false;
      }
    }
  } catch {
    return allCountries;
  }

  return allCountries;
};

export const countryManagementGetById = async (id) => {
  let result;
  await apiService.get(`${apiEndPoints.countryManagementGetById}/${id}`)
    .then((response) => {
      result = response;
    })
    .catch(error => {
      result = error;
    });
  return result;
};

export const deleteCountryManagement = async (id) =>
  attempt(() => apiService.delete(`${apiEndPoints.deleteCountryManagement}/${id}`, ''));

/**
 * Whether this country's geography can be loaded, what it already holds, and how the last load went.
 * Polled while a load is running, so it must stay cheap for the caller to call repeatedly.
 */
export const countryGeoImportStatus = async (id) =>
  attempt(() => apiService.get(`${apiEndPoints.countryGeoImportStatus}/${id}/geo-import/status`));

/**
 * Starts a load. Returns the job either way: already finished for a small country, still running for
 * a large one — the caller decides whether to poll by reading `state`, not by the HTTP code.
 *
 * `overwriteLevelNames` is off unless explicitly asked for: the level names on this screen are the
 * operator's, and a routine reload must not rename them back to whatever the source data calls them.
 */
export const runCountryGeoImport = async (id, overwriteLevelNames = false) =>
  attempt(() => apiService.post(
    `${apiEndPoints.countryGeoImport}/${id}/geo-import?overwriteLevelNames=${overwriteLevelNames}`,
    {}
  ));

// ── manual location editing ────────────────────────────────────────────────
// These sit under /ekutir rather than the shared /geo read API on purpose: creating a location is a
// super-admin act, and a route every app could call is how duplicate blocks got into the data.

const locationsUrl = (countryId) =>
  `${apiEndPoints.countryAdministrativeLocations}/${countryId}/administrative-locations`;

/**
 * The country's top-level locations, or the children of `parentId`. Inactive rows are included —
 * they are what the editor exists to reactivate.
 */
export const listAdministrativeLocations = async (countryId, parentId = null, search = '') => {
  const params = new URLSearchParams();
  if (parentId) params.set('parentId', parentId);
  if (search) params.set('search', search);
  const query = params.toString();
  return attempt(() => apiService.get(`${locationsUrl(countryId)}${query ? `?${query}` : ''}`));
};

export const createAdministrativeLocation = async (countryId, payload) =>
  attempt(() => apiService.post(locationsUrl(countryId), payload));

export const updateAdministrativeLocation = async (countryId, locationId, payload) =>
  attempt(() => apiService.update(`${locationsUrl(countryId)}/${locationId}`, payload));

/** There is no delete — records point at these rows. Deactivating removes it from every picker. */
export const setAdministrativeLocationActive = async (countryId, locationId, active) =>
  attempt(() => apiService.patch(
    `${locationsUrl(countryId)}/${locationId}/status?active=${active}`,
    {}
  ));

/**
 * A bounded preview (plus the true total) of the locations recorded against one administrative
 * level. Backs the "this level still has locations tied to it" confirmation modal shown when
 * removing a level on the country edit screen.
 */
export const getLocationsForAdministrativeLevel = async (countryId, typeId) =>
  attempt(() => apiService.get(
    `${apiEndPoints.countryAdministrativeLocations}/${countryId}/administrative-location-types/${typeId}/locations`
  ));

export const countryManagementMetaData = async (id, editMode) => {
  let result;
  await apiService.get(`${apiEndPoints.countryManagementMetaData}`)
    .then((response) => {
      result = response;
    })
    .catch(error => {
      result = error;
    });
  return result;
};
// ── optional address levels (A-01) ─────────────────────────────────────────
// A level beside the country's own chain (e.g. Gram Panchayat after Block), shown only to the eK Acre
// Growth instances it is switched on for. The country's administrative levels are not touched.

const optionalLevelsUrl = (countryId) =>
  `${apiEndPoints.countryAdministrativeLocations}/${countryId}/optional-levels`;

export const listOptionalLevels = async (countryId) =>
  attempt(async () => (await apiService.get(optionalLevelsUrl(countryId))).data);

export const createOptionalLevel = async (countryId, payload) =>
  attempt(() => apiService.post(optionalLevelsUrl(countryId), payload));

export const updateOptionalLevel = async (countryId, levelId, payload) =>
  attempt(() => apiService.patch(`${optionalLevelsUrl(countryId)}/${levelId}`, payload));

export const setOptionalLevelInstance = async (countryId, levelId, instanceKey, enabled) =>
  attempt(() => apiService.update(
    `${optionalLevelsUrl(countryId)}/${levelId}/instances/${encodeURIComponent(instanceKey)}`,
    { enabled }
  ));

/** Dry run by default: reports what the file would create and which rows match nothing, writing nothing. */
export const importOptionalLevelLocations = async (countryId, levelId, file, dryRun = true) => {
  const form = new FormData();
  form.append('file', file);
  return attempt(() => apiService.post(
    `${optionalLevelsUrl(countryId)}/${levelId}/locations/import?dryRun=${dryRun}`,
    form
  ));
};
